import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { WORK } from "@/constants/testIds";
import WorkImage from "@/components/WorkImage";
import { getCaseStudies } from "@/lib/api";

// Published work changes rarely, but the API can be slow to wake. Keep the last
// good response so returning visitors see real work immediately while we
// revalidate in the background.
const CACHE_KEY = "depmont:case-studies:v1";

function readCache() {
    try {
        const parsed = JSON.parse(localStorage.getItem(CACHE_KEY));
        return Array.isArray(parsed) ? parsed : null;
    } catch {
        return null; // unavailable in private mode — the cache is optional
    }
}

function writeCache(rows) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(rows));
    } catch {
        /* quota or private mode — never fatal */
    }
}

// Stock imagery, used only behind the forthcoming placeholders. It must never
// stand in for a real case study: a stock photograph presented as the studio's
// work is a lie about the portfolio, which is the one thing this section
// cannot do.
const PLACEHOLDER_IMAGES = [
    "https://images.unsplash.com/photo-1517511620798-cec17d428bc0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
    "https://images.unsplash.com/photo-1622396481322-3b83d186701b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
    "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
    "https://images.unsplash.com/photo-1565626424178-c699f6601afd?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
];

const placeholders = [
    { label: "Forthcoming", title: "Identity System" },
    { label: "In Development", title: "Editorial Suite" },
    { label: "Forthcoming", title: "Digital Experience" },
    { label: "In Development", title: "Brand Architecture" },
];

/**
 * One tile in the work grid.
 *
 * Real case studies and the forthcoming placeholders deliberately render
 * through the same component. They used to have separate layouts — the
 * placeholders a staggered grid of 4:5 tiles, real work a stack of full-width
 * chapters — which meant publishing the first case study silently replaced the
 * composition with a different one. One component means the grid stays the
 * grid, whatever happens to be in it.
 *
 * The cover is cropped to 4:5 here, which is the single place in the site that
 * crops client work. Equal tiles are the point of the layout, and this is a
 * listing thumbnail rather than the work itself — every frame inside a case
 * study is still shown whole.
 */
function WorkCard({
    index,
    code,
    topRight,
    eyebrow,
    title,
    image,
    to,
    placeholder = false,
}) {
    const frame = (
        <>
            {image ? (
                <WorkImage
                    src={image}
                    alt={title}
                    fill
                    loading={index < 2 ? "eager" : "lazy"}
                    wrapperClassName="aspect-[4/5] bg-[#1B1B1B]"
                    className={
                        placeholder
                            ? "opacity-70 transition-opacity duration-700 group-hover:opacity-90"
                            : "transition-opacity duration-700 group-hover:opacity-90"
                    }
                />
            ) : (
                // A case study with no cover yet. Deliberately a plain charcoal
                // field rather than stock imagery.
                <div className="img-frame aspect-[4/5] bg-[#1B1B1B]" />
            )}

            {/* Scrim. The placeholders take a heavier vignette because the
                stock imagery is busy; real work only needs enough at the foot
                to keep the title legible. */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: placeholder
                        ? "radial-gradient(ellipse at 50% 45%, rgba(27,27,27,0) 40%, rgba(27,27,27,0.55) 100%)"
                        : "linear-gradient(to top, rgba(27,27,27,0.62) 0%, rgba(27,27,27,0.12) 38%, rgba(27,27,27,0) 62%)",
                }}
            />

            <div className="absolute top-5 left-5 right-5 flex justify-between gap-4 text-[#F6F3EC] font-sans-tight text-[10px] tracking-[0.22em] uppercase pointer-events-none">
                <span>{code}</span>
                <span className="text-right">{topRight}</span>
            </div>

            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4 pointer-events-none">
                <div className="min-w-0">
                    <p className="font-sans-tight text-[10px] tracking-[0.22em] uppercase text-[#F6F3EC]/70 mb-1 truncate">
                        {eyebrow}
                    </p>
                    <p className="font-serif-editorial italic text-2xl md:text-3xl text-[#F6F3EC] leading-[1.1]">
                        {title}
                    </p>
                </div>
                <div className="w-10 h-px bg-[#A8844F] shrink-0 mb-2 transition-all duration-700 group-hover:w-16" />
            </div>
        </>
    );

    return (
        <motion.article
            data-testid={WORK.placeholder(index)}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-8%" }}
            transition={{
                duration: 1.1,
                delay: (index % 2) * 0.08,
                ease: [0.16, 1, 0.3, 1],
            }}
            // The offset column is what gives the grid its rhythm, so it has to
            // follow the column count rather than the index. Two columns offset
            // every second tile; three columns offset the middle one. Both sets
            // of classes are literal so Tailwind's scanner finds them, and the
            // xl rules come later in the sheet, so they win at that width.
            className={`group ${index % 2 === 1 ? "md:mt-28" : ""} ${
                index % 3 === 1 ? "xl:mt-24" : "xl:mt-0"
            }`}
        >
            {to ? (
                <Link to={to} data-testid={`work-link-${index}`} className="block relative">
                    {frame}
                </Link>
            ) : (
                <div className="relative">{frame}</div>
            )}
        </motion.article>
    );
}

/**
 * Neutral placeholder shown only while the real answer is still in flight.
 * Deliberately says nothing about whether work exists — the studio statement
 * about an empty portfolio must never appear unless it is actually true.
 */
function WorkSkeleton() {
    return (
        <div
            data-testid="work-skeleton"
            aria-hidden="true"
            className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-14 animate-pulse"
        >
            {[0, 1].map((i) => (
                <div
                    key={i}
                    className={`aspect-[4/5] bg-[#1B1B1B]/10 ${i % 2 === 1 ? "md:mt-28" : ""}`}
                />
            ))}
        </div>
    );
}

export default function Work() {
    const cached = useMemo(readCache, []);
    const [items, setItems] = useState(cached ?? []);
    // `settled` means we know the real answer — either from cache or the API.
    // Until then we must not claim the portfolio is empty.
    const [settled, setSettled] = useState(cached !== null);

    useEffect(() => {
        let alive = true;
        getCaseStudies()
            .then((rows) => {
                if (!alive) return;
                const list = Array.isArray(rows) ? rows : [];
                setItems(list);
                writeCache(list);
            })
            .catch(() => {
                /* keep whatever the cache gave us */
            })
            .finally(() => {
                if (alive) setSettled(true);
            });
        return () => {
            alive = false;
        };
    }, []);

    const hasWork = items.length > 0;
    const absent = settled && !hasWork;
    const pending = !settled && !hasWork;

    return (
        <section
            id="work"
            data-testid={WORK.section}
            className="py-28 md:py-44 border-t border-[#E6E2D6] bg-[#F1EDE2]"
        >
            <div className="mx-auto max-w-[1800px] px-6 md:px-12 lg:px-20">
                <div className="grid grid-cols-12 gap-6 md:gap-10 mb-20 md:mb-28">
                    <div className="col-span-12 md:col-span-3">
                        <p className="eyebrow text-[#1B1B1B]/70">§ Work</p>
                    </div>
                    <div className="col-span-12 md:col-span-9">
                        <motion.h2
                            data-testid={WORK.heading}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="font-serif-editorial text-5xl md:text-7xl lg:text-[104px] leading-[0.98] tracking-[-0.02em] text-[#1B1B1B] max-w-5xl"
                        >
                            {absent ? (
                                <>
                                    Selected work,{" "}
                                    <em className="italic text-[#A8844F]">
                                        deliberately absent.
                                    </em>
                                </>
                            ) : (
                                <>
                                    Selected{" "}
                                    <em className="italic text-[#A8844F]">work.</em>
                                </>
                            )}
                        </motion.h2>
                    </div>
                </div>

                {absent && (
                    <div className="grid grid-cols-12 gap-6 md:gap-10 mb-20 md:mb-28">
                        <div className="hidden md:block md:col-span-3" />
                        <div className="col-span-12 md:col-span-6 md:col-start-4 space-y-6">
                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true, margin: "-10%" }}
                                transition={{ duration: 1 }}
                                className="font-sans-tight text-lg md:text-xl leading-[1.6] text-[#1B1B1B] max-w-xl font-light"
                            >
                                Depmont launches before its portfolio is complete
                                because reputation should be earned through the
                                work itself, not staged around it.
                            </motion.p>
                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true, margin: "-10%" }}
                                transition={{ duration: 1, delay: 0.1 }}
                                className="font-sans-tight text-base md:text-lg leading-[1.7] text-[#1B1B1B]/80 max-w-xl font-light"
                            >
                                Every published case study will represent work we
                                are proud to stand behind — and that we expect to
                                stand behind a decade from now. Until then, we
                                prefer the honesty of an empty page.
                            </motion.p>
                        </div>
                    </div>
                )}

                {pending ? (
                    <WorkSkeleton />
                ) : (
                    // Three columns above 1280px. The tile is a fixed 4:5, so
                    // its height is decided entirely by its width: at two
                    // columns in an 1800px container a tile is ~790px wide and
                    // therefore ~990px tall, which is taller than the screen it
                    // is being viewed on. Narrowing the tile is the fix — the
                    // ratio has to stay 4:5, because that is what the upload
                    // guidance promises will not be cropped.
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-14">
                        {hasWork
                            ? items.map((row, i) => (
                                  <WorkCard
                                      key={row.id}
                                      index={i}
                                      code={`CS · ${String(i + 1).padStart(3, "0")}`}
                                      topRight={row.category}
                                      eyebrow={row.client || "Case Study"}
                                      title={row.title}
                                      image={row.cover_url}
                                      to={`/work/${row.slug || row.id}`}
                                  />
                              ))
                            : placeholders.map((p, i) => (
                                  <WorkCard
                                      key={p.title}
                                      index={i}
                                      code={`CS · ${String(i + 1).padStart(3, "0")}`}
                                      topRight={p.label}
                                      eyebrow="Case Study"
                                      title={p.title}
                                      image={PLACEHOLDER_IMAGES[i % PLACEHOLDER_IMAGES.length]}
                                      placeholder
                                  />
                              ))}
                    </div>
                )}

                {/* Foot */}
                <div className="mt-24 md:mt-32 border-t border-[#E6E2D6] pt-8 flex flex-col md:flex-row justify-between gap-4">
                    <p className="eyebrow text-[#1B1B1B]/70">
                        {hasWork
                            ? `${items.length} case ${items.length === 1 ? "study" : "studies"} · more forthcoming`
                            : pending
                              ? "Loading…"
                              : "First case study — expected soon"}
                    </p>
                    <a
                        href="mailto:contact@depmont.com"
                        className="link-underline font-sans-tight text-sm text-[#1B1B1B]"
                    >
                        Enquire about upcoming work →
                    </a>
                </div>
            </div>
        </section>
    );
}
